export default function CommunicatingWithTenants() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-chat-3-line"></i>
            <span>Communication Strategy</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Effective Communication Strategies with Current Tenants
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            How you communicate with tenants during the sale process can make or break your transaction. Transparency and professionalism are essential.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/494d6877e6cb7e75bde6574f7a043ad3.jpg"
                alt="Effective communication strategies with tenants during apartment building sale"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">When and How to Notify Tenants</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              The timing and method of notifying tenants about your intention to sell is critical. While you're not legally required to inform tenants before listing the property in most states, doing so proactively can prevent problems and maintain positive relationships.
            </p>

            <div className="space-y-4 mb-8">
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Initial Notification (Before Listing)</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Send a formal letter or email 2-4 weeks before listing the property. Explain your decision to sell, reassure tenants about their lease rights, and outline what to expect during the process.
                    </p>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-600 italic">
                        "We wanted to inform you that we have made the decision to sell the property. Your lease agreement will remain in full effect, and the new owner will honor all terms. We will work to minimize any inconvenience during this transition."
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Showing Schedule Communication</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Provide advance notice for all property showings. Most states require 24-48 hours notice. Use multiple communication channels: written notice, email, text messages, and phone calls.
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-time-line text-teal-600"></i>
                        <span>24-48 hours notice</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-calendar-line text-teal-600"></i>
                        <span>Flexible scheduling</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Under Contract Notification</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Once you have an accepted offer, inform tenants immediately. Introduce the new owner (if appropriate) and provide updated contact information for the transition period.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-600 font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Post-Closing Communication</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Send a final letter with the new owner's complete contact information, payment instructions, and confirmation that all lease terms remain unchanged. This is legally required in most jurisdictions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Communication Best Practices</h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-teal-600 mb-4 flex items-center gap-2">
                <i className="ri-checkbox-circle-fill"></i>
                DO: Recommended Actions
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Be transparent and honest about the sale timeline and process</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Provide written documentation of all communications</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Respond promptly to tenant questions and concerns</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Offer incentives for cooperation (gift cards, rent discounts)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Schedule showings at convenient times for tenants</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Maintain professionalism even if tenants are difficult</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Keep tenants updated on major milestones in the sale process</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-red-600 mb-4 flex items-center gap-2">
                <i className="ri-close-circle-fill"></i>
                DON'T: Actions to Avoid
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Surprise tenants with unannounced showings or inspections</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Make promises about the new owner's intentions or policies</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Pressure tenants to break their leases or move out early</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Share tenant personal information with potential buyers</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Ignore tenant complaints or maintenance requests during the sale</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Use threatening or intimidating language about the sale</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Fail to document communications in writing</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Sample Tenant Notification Letter</h3>
          
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="text-sm text-slate-700 space-y-4">
              <p><strong>Date:</strong> [Current Date]</p>
              <p><strong>To:</strong> All Residents of [Property Name]</p>
              <p><strong>Subject:</strong> Important Notice Regarding Property Sale</p>
              
              <p className="pt-4">Dear Valued Residents,</p>
              
              <p>
                We are writing to inform you that we have made the decision to sell [Property Name]. We wanted you to hear this news directly from us before the property is officially listed for sale.
              </p>
              
              <p>
                <strong>What This Means for You:</strong>
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>Your lease agreement remains in full effect and will be honored by the new owner</li>
                <li>Your rent amount, due date, and all lease terms will not change</li>
                <li>Your security deposit will be transferred to the new owner</li>
                <li>We will continue to handle all maintenance requests until the property closes</li>
              </ul>
              
              <p>
                <strong>What to Expect:</strong>
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>We will provide at least 24 hours notice before any property showings</li>
                <li>Showings will be scheduled at reasonable times and kept to a minimum</li>
                <li>We will introduce you to the new owner before closing</li>
                <li>You will receive written notice with new owner contact information after closing</li>
              </ul>
              
              <p>
                We appreciate your cooperation during this transition. If you have any questions or concerns, please don't hesitate to contact us at [Phone Number] or [Email Address].
              </p>
              
              <p>Thank you for being such wonderful residents.</p>
              
              <p className="pt-4">
                Sincerely,<br />
                [Your Name]<br />
                [Property Management Company]
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
